import Link from '@liveauctioneers/caterwaul-components/lib/Link/Link';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { BodySecondary } from '@liveauctioneers/hammer-ui-core/text';
import { memo } from 'react';
import isEqual from 'react-fast-compare';
import { useLocation } from 'react-router';
import styled, { css } from 'styled-components';

import PageTitle from '@/components/PageTitle/PageTitle';
import NoResults from '@/components/Pagination/NoResults';
import SaveSearchContainer from '@/components/SaveSearch/SaveSearchContainer';
import ZeroResultsContainer from '@/components/ZeroResults/ZeroResultsContainer';
import { useAppSelector } from '@/redux/hooks';
import { getItemSummaryTitle } from '@/redux/modules/item/summary/itemSummary.selectors';
import { SearchParams as SearchParamsType } from '@/types/SaveSearch';
import { useTrackAuctionHouseSuggestionClick } from '@/utils/amplitude/analytics';
import cleanSearchTerms from '@/utils/cleanSearchTerms';
import { preventDefault } from '@/utils/preventDefault';
import { parseQueryParams } from '@/utils/queryParams';
import { safelyEscapeText } from '@/utils/safelyRender';
import { getSellerUrl, isSimilarItemsPage } from '@/utils/urls';

type BuyNowExperimentSearchHeaderProps = {
    showExperiment?: boolean;
};

export type SearchHeaderProps = {
    allSearchParams: SearchParamsType;
    archivedSearch: boolean;
    firstHouse: any;
    isMobileView: boolean;
    liveItemsFound?: number;
    saveSearchParams: SearchParamsType;
    searchTerm: string;
    showNoResults: boolean;
    whiteLabel?: boolean;
} & BuyNowExperimentSearchHeaderProps;

const SearchHeader = ({
    allSearchParams,
    archivedSearch,
    firstHouse,
    isMobileView,
    liveItemsFound,
    saveSearchParams,
    searchTerm,
    showExperiment,
    showNoResults,
    whiteLabel,
}: SearchHeaderProps) => {
    const { formatMessage } = useIntl();

    const { pathname, search } = useLocation();
    const queryParams = parseQueryParams<{ itemId?: number }>(search);
    const similarItemsPage = isSimilarItemsPage(pathname);
    const safeSearchTerm = cleanSearchTerms(searchTerm);
    const similarItemTerm = cleanSearchTerms(
        useAppSelector((state) => getItemSummaryTitle(state, queryParams?.itemId))
    );
    const trackAuctionHouseSuggestionClick = useTrackAuctionHouseSuggestionClick();

    const zeroResultsFromTerm = showNoResults && !archivedSearch && Boolean(safeSearchTerm) && (
        <StyledZeroResultsContainer
            $noLiveItems={!liveItemsFound}
            allSearchParams={allSearchParams}
            saveSearchParams={saveSearchParams}
            searchTerm={safeSearchTerm}
        />
    );
    const noResults = showNoResults && (!safeSearchTerm || archivedSearch) && (
        <StyledNoResults>
            <NoResults>
                <FormattedMessage id="search_no_results" />
            </NoResults>
        </StyledNoResults>
    );
    const whiteLabelSuggestion = firstHouse && !whiteLabel && (
        <StyledSuggestion>
            <FormattedMessage
                id="searchResults.lookingFor"
                values={{
                    suggestion: (
                        <Link
                            key="house-link"
                            onClick={() => trackAuctionHouseSuggestionClick({ houseId: firstHouse.houseId })}
                            route
                            to={getSellerUrl(firstHouse.houseId, firstHouse.value)}
                        >
                            {safelyEscapeText(firstHouse.value)}
                        </Link>
                    ),
                }}
            />
        </StyledSuggestion>
    );

    const showBuyNowTitle = showExperiment && searchTerm === '';

    const showSearchTitle = searchTerm && searchTerm !== '' && !showBuyNowTitle;

    return (
        <StyledHeaderContainer>
            <StyledControls onSubmit={preventDefault}>
                <StyledResultsWrapper>
                    {zeroResultsFromTerm}
                    {noResults}
                    {whiteLabelSuggestion}
                    {!showNoResults && (
                        <StyledSearchTermsRow>
                            {showSearchTitle && (
                                <StyledSearchTermsContainer>
                                    <PageTitleRow>
                                        <StyledPageTitle
                                            title={
                                                <FormattedMessage
                                                    id={similarItemsPage ? 'moreLikeResults' : 'searchResultsFor'}
                                                    values={{
                                                        searchTerm: similarItemsPage ? similarItemTerm : safeSearchTerm,
                                                    }}
                                                />
                                            }
                                        />
                                    </PageTitleRow>
                                    {!isMobileView && (
                                        <SaveSearchRow>
                                            <SaveSearchContainer
                                                allSearchParams={allSearchParams}
                                                className="searchOptionsSaveButton"
                                                saveSearchParams={saveSearchParams}
                                                small
                                                tooltipPlacement="bottom"
                                            />
                                        </SaveSearchRow>
                                    )}
                                </StyledSearchTermsContainer>
                            )}
                            {showBuyNowTitle && (
                                <StyledSearchTermsContainer>
                                    <PageTitleRow>
                                        <StyledPageTitle title={<FormattedMessage id="buyNowExperiment.pageTitle" />} />
                                    </PageTitleRow>
                                    <section>
                                        <BodySecondary>
                                            {formatMessage({ id: 'buyNowExperiment.pageSubtitle' })}
                                        </BodySecondary>
                                    </section>
                                </StyledSearchTermsContainer>
                            )}
                        </StyledSearchTermsRow>
                    )}
                </StyledResultsWrapper>
            </StyledControls>
        </StyledHeaderContainer>
    );
};

export default memo<SearchHeaderProps>(SearchHeader, isEqual);

const PageTitleRow = styled.div`
    align-items: baseline;
    display: flex;
    flex-direction: row;
    margin-bottom: 16px;
`;

const SaveSearchRow = styled.div`
    ${({ theme }) => css`
        height: 30px;
        max-width: 247px;
        .searchOptionsSaveButton {
            max-width: 247px;
            width: 100%;
        }

        @media (max-width: ${theme.breakpoints.tabletNarrowWidth}) {
            max-width: 253px;
            .searchOptionsSaveButton {
                max-width: 253px;
                width: 100%;
            }
        }
    `}
`;

const StyledHeaderContainer = styled.div`
    flex: 0 1 auto;
    margin: 0 0 16px;

    @media (min-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidthNum + 1}px) {
        margin: 0 0 64px;
    }
`;

const StyledControls = styled.div`
    margin-top: 32px;
    flex: 0 0 auto;
    width: 100%;
    padding: 0;
    background-color: ${({ theme }) => theme.colors.white};
    display: flex;
    flex-direction: row;
`;

const StyledResultsWrapper = styled.div`
    position: relative;
    width: 100%;
    display: flex;
    flex-direction: column;
`;

const StyledSearchTermsRow = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;

    @media (min-width: 610px) {
        align-items: center;
        flex-direction: row;
    }
`;

const StyledSearchTermsContainer = styled.div`
    width: 100%;

    @media (min-width: 416px) {
        margin-top: 0;
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.tabletNarrowWidth}) {
        min-width: 0;
    }
`;

const StyledNoResults = styled.div`
    margin-top: 30px;
    width: 100%;
`;

const StyledSuggestion = styled.div`
    margin-bottom: 10px;
`;

const StyledPageTitle = styled(PageTitle)`
    && {
        margin: 0;
    }
`;

const StyledZeroResultsContainer = styled(ZeroResultsContainer)<{
    $noLiveItems: boolean;
}>`
    ${({ $noLiveItems }) =>
        $noLiveItems &&
        css`
            display: flex;
            flex-direction: column;
        `}
`;
